import "@xyflow/react/dist/style.css";
import { agentShortName } from "@baton/contracts";
import type { DecryptedProjectGraph, DecryptedProjectInvitation } from "./projects-view.js";
import type { BatonNode } from "./flow/model.js";
import type { ActivityEvent } from "./flow/activity.js";
import { useFlowActions } from "./flow/nodes.js";
import { FlowCanvas, type FlowViewer } from "./flow/flow-canvas.js";
import type { PositionChange } from "./hooks/use-canvas-positions.js";

interface FlowHomeProps {
  hasProjects: boolean;
  invitations: DecryptedProjectInvitation[];
  graph: DecryptedProjectGraph | null;
  selectedProjectId: string | null;
  viewer: FlowViewer;
  loading: boolean;
  listView: boolean;
  positions: ReadonlyMap<string, { x: number; y: number }>;
  onPositionsChange(changes: PositionChange[]): void;
  onTidy(positions: PositionChange[]): void;
  events: ActivityEvent[];
  onSelect(node: BatonNode | null): void;
  onCreateProject(): void;
  onAcceptInvitation(projectId: string): void;
}

/** The stage: the project canvas filling the workspace, an accessible list, or the onboarding empty state. */
export function FlowHome(props: FlowHomeProps) {
  if (!props.hasProjects) {
    return <FlowEmptyState
      loading={props.loading}
      invitations={props.invitations}
      onCreateProject={props.onCreateProject}
      onAcceptInvitation={props.onAcceptInvitation}
    />;
  }
  if (!props.graph) {
    return <section className="flow-stage flow-stage-empty"><p className="empty">{props.loading ? "Loading the project graph…" : "Select a project."}</p></section>;
  }
  if (props.listView) {
    return <section className="flow-stage"><FlowList graph={props.graph} viewer={props.viewer} /></section>;
  }
  return <section className="flow-stage">
    <FlowCanvas
      key={props.selectedProjectId ?? "none"}
      graph={props.graph}
      viewer={props.viewer}
      positions={props.positions}
      onPositionsChange={props.onPositionsChange}
      onTidy={props.onTidy}
      events={props.events}
      onSelectionChange={props.onSelect}
      ariaLabel={`Project graph for ${props.graph.project.name}`}
    />
  </section>;
}

function FlowList({ graph, viewer }: { graph: DecryptedProjectGraph; viewer: FlowViewer }) {
  const actions = useFlowActions();
  const agents = new Map<string, { handle: string; role: string | null; agentId: string; repositories: string[]; projectWide: boolean; ownedByViewer: boolean; owner: string | undefined }>();
  for (const assignment of graph.agentAssignments) {
    const owner = graph.members.find((member) => member.userId === assignment.agent.ownerUserId)?.handle;
    const entry = agents.get(assignment.agent.id) ?? {
      owner,
      handle: agentShortName(assignment.agent, owner),
      role: assignment.agent.role,
      agentId: assignment.agent.id,
      repositories: [],
      projectWide: false,
      ownedByViewer: assignment.agent.ownerUserId === viewer.userId,
    };
    if (assignment.repositoryId === null) entry.projectWide = true;
    else {
      const repository = graph.repositories.find((candidate) => candidate.id === assignment.repositoryId);
      if (repository) entry.repositories.push(`${repository.ownerHandle}/${repository.name}`);
    }
    agents.set(assignment.agent.id, entry);
  }
  const sorted = [...agents.values()].sort((left, right) => left.handle.localeCompare(right.handle));
  return <div className="flow-list" data-testid="flow-list">
    <h4>{graph.project.name}</h4>
    <p className="muted">Accessible list of the same server-filtered graph.</p>
    <p className="flow-list-branch">Git repositories</p>
    <ul>
      {graph.repositories.map((repository) => <li key={repository.id}>
        <span>{repository.ownerHandle}/{repository.name}</span>
        <span className="muted"> · {repository.permission} · {repository.defaultBranch}</span>
      </li>)}
      {graph.repositories.length === 0 && <li className="muted">No visible repositories</li>}
    </ul>
    <p className="flow-list-branch">Agents</p>
    <ul>
      {sorted.map((agent) => <li key={agent.agentId}>
        <span>{agent.handle}{agent.owner ? ` · @${agent.owner}` : ""}{agent.role && agent.role !== agent.handle ? ` · ${agent.role}` : ""}</span>
        <span className="muted"> · {agent.projectWide ? "whole project" : agent.repositories.join(", ")}</span>
        {agent.agentId !== viewer.generalAgentId && <button type="button" className="link" onClick={() => actions.chat({ agentId: agent.agentId, handle: agent.handle })}>Chat</button>}
        {agent.ownedByViewer && <button type="button" className="link" onClick={() => actions.connect({ agentId: agent.agentId, handle: agent.handle })}>Connect</button>}
      </li>)}
      {sorted.length === 0 && <li className="muted">No assigned agents</li>}
    </ul>
    <p className="flow-list-branch">Conversations</p>
    <ul>
      {(graph.canvas?.wires ?? []).map((wire) => {
        const source = agents.get(wire.sourceAgentId);
        const target = agents.get(wire.targetAgentId);
        if (!source || !target) return null;
        return <li key={wire.id}>
          <span>{source.handle} ⟷ {target.handle}</span>
          <button type="button" className="link" onClick={() => actions.openWire({ wireId: wire.id, sourceAgentId: wire.sourceAgentId, targetAgentId: wire.targetAgentId })}>Open</button>
        </li>;
      })}
      {(graph.canvas?.wires.length ?? 0) === 0 && <li className="muted">No wires yet</li>}
    </ul>
  </div>;
}

function FlowEmptyState(props: {
  loading: boolean;
  invitations: DecryptedProjectInvitation[];
  onCreateProject(): void;
  onAcceptInvitation(projectId: string): void;
}) {
  return <section className="flow-stage flow-stage-empty">
    <div className="flow-empty panel">
      <p className="eyebrow">Flow</p>
      <h3>{props.loading ? "Loading your authorized graph…" : "No project graph yet"}</h3>
      <ol className="flow-sequence">
        <li>Create a project, or accept an invitation.</li>
        <li>Add your GitHub repositories and invite their collaborators.</li>
        <li>Your general agent is assigned automatically.</li>
        <li>Hand over your work. Your collaborator picks it up from Telegram.</li>
      </ol>
      {props.invitations.length > 0 && <ul className="invite-list">
        {props.invitations.map((invitation) => <li key={invitation.projectId}>
          <span><strong>{invitation.name}</strong> · from {invitation.invitedByHandle}</span>
          <button type="button" onClick={() => props.onAcceptInvitation(invitation.projectId)}>Accept invitation</button>
        </li>)}
      </ul>}
      <div className="flow-empty-actions">
        <button type="button" className="primary" onClick={props.onCreateProject}>Create project</button>
      </div>
    </div>
  </section>;
}
